import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { toPercentage } from "@common/utilities";
import { useRiskAreasContext } from "@common/providers/riskAreasProvider/RiskAreasProvider";
import { RiskDashboardResult } from "./RiskDashboardWidget.types";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { NavigationMenuItem } from "../../navigationMenuSettings/NavigationMenuSettings.types";
import { isNavigationMenuItemHidden } from "../../navigationMenuSettings/navigationMenuItems";

export interface IRiskDashboardWidgetProps {
    onlyUserSpecific: boolean;
    selectedGroupCompany: string | undefined;
    isPdf?: boolean;
}

// Both call sites only render the widget for customers with Enterprise Risk Management, so the permission is not
// re-checked here — the risk-areas provider is gated on it too.
export function useRiskDashboardWidget(props: IRiskDashboardWidgetProps) {
    const { riskAreas, isLoading: isLoadingRiskAreas } = useRiskAreasContext();
    const { hiddenNavigationMenuItems } = useUserContext();
    const isEnabled = !isNavigationMenuItemHidden(hiddenNavigationMenuItems, NavigationMenuItem.Risk);

    const { isLoading: isLoadingDashboard, data } = useDotLegalQuery<RiskDashboardResult>(
        buildDashboardUrl(props.onlyUserSpecific, props.selectedGroupCompany),
        { enabled: isEnabled }
    );

    const risksOverAppetite = data?.risksOverAppetite ?? 0;
    const riskAppetiteLimit = data?.maxRisksOverAppetite ?? 0;

    return {
        isEnabled,
        isLoading: isLoadingRiskAreas || isLoadingDashboard,
        // Floored, never rounded, so the bar never claims 100% before every entity is actually assessed. The
        // percentage bar prints the number verbatim, hence the rounding has to happen here.
        assessedPercentage: Math.floor(toPercentage(data?.assessedEntities ?? 0, data?.totalEntities ?? 0)),
        assessedEntities: data?.assessedEntities ?? 0,
        totalEntities: data?.totalEntities ?? 0,
        risksOverAppetite,
        riskAppetiteLimit,
        // The same rule as the management report's card, so the two never disagree about the same pair of numbers.
        isExceeded: risksOverAppetite > riskAppetiteLimit,
        topRisks: data?.topRisks ?? [],
        hasNoRiskAreas: !isLoadingRiskAreas && riskAreas.length === 0,
    };
}

// The group company is appended only when one is selected, so an unfiltered dashboard does not send the string
// "undefined" as a query value.
function buildDashboardUrl(onlyUserSpecific: boolean, selectedGroupCompany: string | undefined) {
    const params = new URLSearchParams({ onlyUserSpecific: onlyUserSpecific.toString() });
    if (selectedGroupCompany) {
        params.set("forGroupCompany", selectedGroupCompany);
    }

    return `/risk-dashboard?${params.toString()}`;
}
